/**
 * Sale-day work modes (ADR-44) - what the employee is DOING right now, as
 * distinct from what their account is ALLOWED to do.
 *
 * Security roles stay broad (admin / office / clerk / cataloger / consignor)
 * and live in Cognito; work modes are fine-grained stations and live HERE, as
 * product configuration. A mode maps to the page that serves that station and
 * to the roles that may work it - LEAST privileged first, so an admin working
 * checkout runs as `office` and sees exactly what the regular clerk sees
 * (MANAGER APPROVAL NEEDED, not the override). Wanting the manager controls
 * is a deliberate act: switch to Sale Office.
 *
 * Everything here is pure: roles in, decisions out. Authority is enforced
 * server-side (x-active-staff-role verified against assured memberships);
 * these functions only decide what to OFFER.
 */

export interface WorkMode {
  readonly key: string;
  readonly label: string;
  readonly desc: string;
  readonly page: string;
  /** Roles that may work this station, LEAST privileged first. */
  readonly roles: readonly string[];
}

export const WORK_MODES: readonly WorkMode[] = [
  // "Ring Clerk" is the station: this person is running the LIVE auction.
  // Named against the future Paper Clerk, who only records results the
  // auctioneer calls - the distinction is the whole point of ADR-44. "Ring · Clerk
  // Console" named the screen, which is our word for it, not theirs.
  { key: 'ring', label: 'Ring Clerk', desc: 'Run the live auction', page: 'index.html', roles: ['clerk', 'admin'] },
  // The other clerking job entirely (brief section 10): the auctioneer runs
  // the sale out loud and this person records what was called. Same roles as
  // Ring Clerk because it is the same authority - a different station, not a
  // different permission.
  { key: 'paper', label: 'Paper Clerk', desc: 'Record results at speed', page: 'paper.html', roles: ['clerk', 'admin'] },
  // Morning stations (brief sections 5 and 27). Roles mirror the routes each
  // one actually calls: walk-up registration is FLOOR_STAFF (office/clerk/
  // admin), and marking a horse arrived is a lot edit (LOT_EDIT), which the
  // cataloger holds too - the person receiving horses at 7am is often the
  // person who catalogued them.
  { key: 'buyer-checkin', label: 'Buyer Check-In', desc: 'Register bidders & issue numbers', page: 'buyer-checkin.html', roles: ['clerk', 'office', 'admin'] },
  { key: 'horse-checkin', label: 'Horse Check-In', desc: 'Receive horses & confirm catalog numbers', page: 'horse-checkin.html', roles: ['clerk', 'cataloger', 'office', 'admin'] },
  { key: 'checkout', label: 'Checkout', desc: 'Take payments & release purchases', page: 'checkout.html', roles: ['office', 'admin'] },
  { key: 'gate', label: 'Gate / Release', desc: 'Scan paddles & release horses', page: 'gate.html', roles: ['clerk', 'cataloger', 'office', 'admin'] },
  { key: 'office', label: 'Sale Office', desc: 'Manager approvals & exceptions', page: 'checkout.html', roles: ['admin'] },
  { key: 'payouts', label: 'Seller Payouts', desc: 'Consignor statements & payouts', page: 'consignor.html', roles: ['office', 'admin'] },
];

/** The stations this person can work, given every role they hold. */
export function availableModes(roles: readonly string[]): WorkMode[] {
  return WORK_MODES.filter((m) => m.roles.some((r) => roles.includes(r)));
}

/** The role a mode runs as: the LEAST privileged one the person holds. */
export function roleForMode(mode: WorkMode, roles: readonly string[]): string | null {
  return mode.roles.find((r) => roles.includes(r)) ?? null;
}

export function modeByKey(key: string | null | undefined): WorkMode | null {
  return WORK_MODES.find((m) => m.key === key) ?? null;
}

/**
 * The mode this page-load is operating in. `?mode=` wins when it names a mode
 * this person can work AND it belongs on this page; else the page's own
 * default. Keeps a pasted URL from labelling a checkout tab "Gate".
 */
export function activeMode(params: Pick<URLSearchParams, 'get'>, roles: readonly string[], pageDefault: string): WorkMode {
  const want = modeByKey(params.get('mode'));
  const fallback = modeByKey(pageDefault)!;
  if (!want) return fallback;
  if (want.page !== fallback.page) return fallback;
  return roleForMode(want, roles) ? want : fallback;
}

/** Where a mode lives, carrying the session params and pinning mode + role. */
export function modeHref(mode: WorkMode, search: string, roles: readonly string[]): string {
  const params = new URLSearchParams(search);
  params.set('mode', mode.key);
  const as = roleForMode(mode, roles);
  if (as) params.set('as', as);
  else params.delete('as');
  return `${mode.page}?${params.toString()}`;
}

/** Login rule: one station → go there; several → ask; none → say so. */
export function launchDecision(roles: readonly string[]):
  | { kind: 'none' }
  | { kind: 'go'; mode: WorkMode }
  | { kind: 'choose'; modes: WorkMode[] } {
  const modes = availableModes(roles);
  if (modes.length === 0) return { kind: 'none' };
  if (modes.length === 1) return { kind: 'go', mode: modes[0]! };
  return { kind: 'choose', modes };
}
